import Image from "next/image";
import { Mail, MapPin } from "lucide-react";
import { FaFacebookF, FaInstagram } from "react-icons/fa";
import type { ReactNode } from "react";
import { formatCep } from "@/lib/cep";
import type { AcceptedCardBrand } from "@/types/company";
import type { PublicCompany } from "@/types/store";
import styles from "./dedicated-footer.module.css";
import { dedicatedStoreTheme } from "./dedicated-store-theme";

const CARD_PAYMENT_IMAGES: Record<
  AcceptedCardBrand,
  { src: string; alt: string; width: number; height: number }
> = {
  visa: { src: "/payments/visa.png", alt: "Visa", width: 294, height: 99 },
  mastercard: {
    src: "/payments/mastercard.png",
    alt: "Mastercard",
    width: 485,
    height: 378,
  },
  elo: { src: "/payments/elo.png", alt: "Elo", width: 3500, height: 1339 },
  amex: {
    src: "/payments/American-Express.png",
    alt: "American Express",
    width: 1270,
    height: 375,
  },
  hipercard: {
    src: "/payments/hipercard.png",
    alt: "Hipercard",
    width: 294,
    height: 131,
  },
};

type Props = {
  company: PublicCompany;
  primaryColor: string;
  fallbackColor: string;
  containerClassName: string;
  institutional: ReactNode;
  services: ReactNode;
  servicesLabel: string;
  instagramUrl: string;
  facebookUrl: string;
  showCreci?: boolean;
  showCashPayment?: boolean;
};

export default function DedicatedStoreFooter({
  company,
  primaryColor,
  fallbackColor,
  containerClassName,
  institutional,
  services,
  servicesLabel,
  instagramUrl,
  facebookUrl,
  showCreci = false,
  showCashPayment = false,
}: Props) {
  const clean = (value: string) => value.trim().replace(/\s+/g, " ");
  const firstLine = [
    [company.address, company.number].map(clean).filter(Boolean).join(", "),
    clean(company.district),
  ]
    .filter(Boolean)
    .join(" - ");
  const cep = formatCep(company.cep);
  const secondLine = [
    cep && `CEP ${cep}`,
    [company.city, company.state].map(clean).filter(Boolean).join(" - "),
  ]
    .filter(Boolean)
    .join(" • ");
  const email = company.email.trim();
  const socials = [
    { label: "Instagram", url: instagramUrl.trim(), Icon: FaInstagram },
    { label: "Facebook", url: facebookUrl.trim(), Icon: FaFacebookF },
  ].filter(({ url }) => /^https?:\/\//i.test(url));
  const hasPayments =
    company.accept_pix || company.accept_card_on_delivery ||
    (showCashPayment && company.accept_cash_on_delivery);

  return (
    <footer
      className={styles.footer}
      style={dedicatedStoreTheme(primaryColor, fallbackColor)}
    >
      <div className={`${containerClassName} ${styles.columns}`}>
        <div className={styles.identity}>
          <div className={styles.identityRow}>
            {company.logo_url && (
              <img
                src={company.logo_url}
                alt=""
                loading="lazy"
                decoding="async"
              />
            )}
            <div>
              <p className={styles.name}>{company.name}</p>
              {showCreci && company.creci_number?.trim() && (
                <p className={styles.creci}>
                  CRECI {company.creci_number.trim()}
                </p>
              )}
            </div>
          </div>
          {(firstLine || secondLine) && (
            <address className={styles.contact}>
              <MapPin size={16} aria-hidden="true" />
              <span>
                {firstLine && (
                  <span className={styles.addressLine}>{firstLine}</span>
                )}
                {secondLine && (
                  <span className={styles.addressLine}>{secondLine}</span>
                )}
              </span>
            </address>
          )}
          {email && (
            <a href={`mailto:${email}`} className={styles.contact}>
              <Mail size={16} aria-hidden="true" />
              <span>{email}</span>
            </a>
          )}
        </div>
        <nav aria-label="Navegação do rodapé">
          <h2>Institucional</h2>
          {institutional}
        </nav>
        <nav aria-label={servicesLabel}>
          <h2>Serviços</h2>
          {services}
        </nav>
        <nav aria-label="Redes sociais">
          <h2>Redes sociais</h2>
          {socials.length > 0 && (
            <div className={styles.socials}>
              {socials.map(({ label, url, Icon }) => (
                <a
                  key={label}
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={`Visitar ${label} da empresa`}
                  title={label}
                >
                  <Icon size={19} aria-hidden="true" />
                </a>
              ))}
            </div>
          )}
        </nav>
      </div>
      {hasPayments && (
        <div className={`${containerClassName} ${styles.paymentMethods}`}>
          <p className={styles.paymentTitle}>Formas de pagamento</p>
          <div className={styles.payments} aria-label="Formas de pagamento">
            {company.accept_pix && (
              <span className={styles.paymentCard}>
                <Image
                  src="/payments/pix.png"
                  alt="Pix"
                  width={954}
                  height={339}
                  sizes="40px"
                  className={styles.paymentLogo}
                />
              </span>
            )}

            {company.accept_card_on_delivery &&
              (company.accepted_card_brands.length > 0 ? (
                company.accepted_card_brands.map((brand) => {
                  const payment = Object.hasOwn(CARD_PAYMENT_IMAGES, brand)
                    ? CARD_PAYMENT_IMAGES[brand]
                    : undefined;

                  return payment ? (
                    <span key={brand} className={styles.paymentCard}>
                      <Image
                        {...payment}
                        sizes="40px"
                        className={styles.paymentLogo}
                      />
                    </span>
                  ) : null;
                })
              ) : (
                <span className={`${styles.paymentCard} ${styles.paymentFallback}`}>
                  Cartão
                </span>
              ))}
            {showCashPayment && company.accept_cash_on_delivery && (
              <span className={`${styles.paymentCard} ${styles.paymentFallback}`}>
                Dinheiro
              </span>
            )}
          </div>
        </div>
      )}

      <div className={styles.bottom}>
        <div className={`${containerClassName} ${styles.legal}`}>
          <p>
            © {new Date().getFullYear()} {company.name} • Todos os direitos
            reservados
          </p>
          {/* Both dedicated footers already retain this attribution for all plans. */}
          <p className={styles.brand}>
            <span>Desenvolvido por</span>
            <span className={styles.platformIcon} aria-hidden="true">
              <Image
                src="/images/logo-icon-fornecedor.png"
                alt=""
                width={713}
                height={779}
                sizes="12px"
              />
            </span>
            <a href="https://fornecedor.net" target="_blank" rel="noreferrer">
              Fornecedor.Net
            </a>
          </p>
        </div>
      </div>
    </footer>
  );
}
